export const VOUCHER_TYPES = [
  { id: 'payment', label: 'Payment Voucher', prefix: 'PV' },
  { id: 'receipt', label: 'Receipt Voucher', prefix: 'RV' },
  { id: 'journal', label: 'Journal Voucher', prefix: 'JV' },
  { id: 'contra', label: 'Contra Voucher', prefix: 'CV' }
];

export const PAYMENT_METHODS = ['Cash', 'Cheque', 'Bank Transfer', 'Online'];

export const getToday = () => new Date().toISOString().split('T')[0];

export const newLineId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export const emptyLine = () => ({
  id: newLineId(),
  accountCode: '',
  accountName: '',
  amount: ''
});

export const createEmptyHeader = (voucherType, voucherNumber, date = getToday()) => ({
  voucherType,
  voucherNumber,
  date,
  party: '',
  paymentMethod: 'Bank Transfer',
  description: '',
  reference: '',
  notes: '',
  branchCode: '',
  branchAccount: '',
  branchName: '',
  chequeNumber: ''
});

export const toYmdCompact = (dateYmd) =>
  String(dateYmd || getToday()).substring(0, 10).replace(/-/g, '');

export const generateAccountingVoucherNumber = (voucherType, dateYmd, existingRecords = []) => {
  const typeMeta = VOUCHER_TYPES.find((t) => t.id === voucherType) || VOUCHER_TYPES[0];
  const datePart = toYmdCompact(dateYmd);
  const prefix = typeMeta.prefix;
  const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`^${escapedPrefix}-${datePart}-(\\d{3})$`, 'i');

  let maxSeq = 0;
  (existingRecords || []).forEach((record) => {
    const recordType = record?.voucher_type || record?.voucherType;
    if (recordType !== voucherType) return;
    const vn = String(record?.voucher_number || record?.voucherNumber || '').trim();
    const match = vn.match(pattern);
    if (match) {
      const seq = parseInt(match[1], 10);
      if (Number.isFinite(seq) && seq > maxSeq) maxSeq = seq;
    }
  });

  return `${prefix}-${datePart}-${String(maxSeq + 1).padStart(3, '0')}`;
};

export const parseAmount = (raw) => {
  const n = parseFloat(String(raw || '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const lineIsFilled = (line) =>
  Boolean(String(line?.accountCode || '').trim()) && parseAmount(line?.amount) > 0;

export const sumLines = (lines) =>
  (lines || []).reduce(
    (sum, line) => sum + (lineIsFilled(line) ? parseAmount(line.amount) : 0),
    0
  );

export const validateSideLines = (lines, sideLabel) => {
  const filled = (lines || []).filter(lineIsFilled);
  if (filled.length === 0) {
    return { ok: false, message: `Add at least one ${sideLabel} line with GL code and amount.` };
  }
  const incomplete = (lines || []).some((line) => {
    const hasCode = Boolean(String(line.accountCode || '').trim());
    const hasAmt = parseAmount(line.amount) > 0;
    return (hasCode && !hasAmt) || (!hasCode && hasAmt);
  });
  if (incomplete) {
    return { ok: false, message: `Each ${sideLabel} line needs both GL code and amount.` };
  }
  return { ok: true, filled };
};

export const validateJournal = (debitLines, creditLines) => {
  const dr = validateSideLines(debitLines, 'debit');
  if (!dr.ok) return dr;
  const cr = validateSideLines(creditLines, 'credit');
  if (!cr.ok) return cr;
  const drTotal = sumLines(debitLines);
  const crTotal = sumLines(creditLines);
  if (Math.abs(drTotal - crTotal) >= 0.01) {
    return {
      ok: false,
      message: `Debits (${drTotal.toFixed(2)}) must equal credits (${crTotal.toFixed(2)}).`
    };
  }
  return { ok: true, drTotal, crTotal };
};

// Builds the POST /api/vouchers payload consumed by routes/vouchers.js.
export const buildVoucherPayload = ({ voucherType, header, debitLines, creditLines, userEmail }) => {
  const toLinePayload = (lines) =>
    (lines || [])
      .filter(lineIsFilled)
      .map((line) => ({
        accountCode: String(line.accountCode || '').trim(),
        amount: parseAmount(line.amount)
      }));

  const noteParts = [];
  if (header.notes?.trim()) noteParts.push(header.notes.trim());
  if (header.chequeNumber?.trim()) noteParts.push(`Cheque: ${header.chequeNumber.trim()}`);

  return {
    voucherNumber: header.voucherNumber,
    voucherType,
    voucherDate: header.date,
    party: header.party?.trim() || null,
    paymentMethod: header.paymentMethod || null,
    description: header.description || '',
    reference: header.reference || '',
    notes: noteParts.length > 0 ? noteParts.join(' | ') : null,
    branchCode: header.branchCode?.trim() || null,
    branchAccount: header.branchAccount?.trim() || null,
    branchName: header.branchName?.trim() || null,
    chequeNumber: header.chequeNumber?.trim() || null,
    debitLines: toLinePayload(debitLines),
    creditLines: toLinePayload(creditLines),
    createdBy: userEmail || null
  };
};

export const getPartyLabel = (voucherType) => {
  switch (voucherType) {
    case 'receipt':
      return 'Received from';
    case 'journal':
      return 'Reference party (optional)';
    case 'contra':
      return 'Transfer party (optional)';
    default:
      return 'Payee';
  }
};

export const showsBankSection = (voucherType) => voucherType !== 'journal';

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(amount) || 0);

export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—';
  const raw = String(dateStr).substring(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const getVoucherTypeLabel = (typeId) =>
  VOUCHER_TYPES.find((t) => t.id === typeId)?.label || typeId;

export const getVoucherTypeLabelFromRecord = (record) =>
  getVoucherTypeLabel(record?.voucher_type || record?.voucherType);

// Splits the flat GL lines returned by GET /api/vouchers/:voucherNumber into
// debit/credit display arrays for the detail modal.
export const mapVoucherLinesToJournalLines = (lines = []) => {
  const debitLines = [];
  const creditLines = [];
  (lines || []).forEach((entry) => {
    const debit = parseFloat(entry.debit_amount) || 0;
    const credit = parseFloat(entry.credit_amount) || 0;
    const line = {
      accountCode: entry.account_code || '',
      accountName: entry.account_name || '',
      amount: debit > 0 ? debit : credit
    };
    if (debit > 0) debitLines.push(line);
    if (credit > 0) creditLines.push(line);
  });
  return { debitLines, creditLines };
};
